import {
  Component,
  EventEmitter,
  Input,
  Output,
  viewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FileUploaderComponent } from '@pishgamrayan/ng-form-kit';
import { Observable, finalize } from 'rxjs';
import { BaseFormModule } from 'src/app/pages/shared/base';
import {
  CustomResult,
  HelpFileTypes,
  MenuHelp,
} from '../../../../core/api/models';
import { ListGroupItemModule } from '../../../global/list-group-item/list-group-item.module';
import { AddApiUrlBeforePathPipe } from './../../../../core/pipes/add-api-url-before-path.pipe';

@Component({
    selector: 'app-file-card',
    templateUrl: './file-card.component.html',
    imports: [BaseFormModule, AddApiUrlBeforePathPipe, ListGroupItemModule]
})
export class FileCardComponent {
  //#region props
  readonly fileUploader = viewChild<FileUploaderComponent>('fileUploader');

  @Input() fileItem: MenuHelp;
  @Output() fileItemChange = new EventEmitter<MenuHelp>();
  @Input() service: Object;
  @Input() methodName: string;
  @Input() filePropName: string = 'File';
  @Output() deleteCard = new EventEmitter<any>();
  file: Blob;
  form: FormGroup;
  previewFileUrl: string = '';
  allowedFileTypes: string[] = [];
  loading: boolean = false;
  get HelpFileTypes() {
    return HelpFileTypes;
  }
  isUploadedFile: boolean = false;

  //#endregion

  ngOnInit(): void {
    this.initForm();
    if (this.fileItem.filePath) this.isUploadedFile = true;
    this.selectFileType(false);
    this.selectPreview();
  }

  initForm() {
    this.form = new FormGroup({
      title: new FormControl('', [
        Validators.required,
        Validators.maxLength(255),
      ]),
      type: new FormControl('', [Validators.required]),
      file: new FormControl('', [Validators.required]),
    });
  }

  onDeleteCard(): void {
    this.deleteCard.emit(this.fileItem);
  }

  emitChanges() {
    this.fileItemChange.emit(this.fileItem);
  }

  uploadFileApi() {
    if (this.loading) return;
    this.loading = true;
    const body = {
      [this.filePropName]: this.file,
    };
    (
      this.service[this.methodName]({
        body,
      }) as Observable<any>
    )
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((res) => {
        if ((res as CustomResult).isSucceed) {
          this.fileItem.filePath = res?.['data'];
          this.isUploadedFile = true;
          this.selectPreview();
          this.emitChanges();
        }
      });
  }

  onUploadApi(): void {
    if (this.form.invalid) return;
    this.uploadFileApi();
  }

  onFileChange(file: any): void {
    if (!file) {
      this.previewFileUrl = '';
      return;
    }
    this.file = file;
    this.selectPreview();
  }

  selectPreview() {
    const fileIconBaseAddr = 'assets/media/svg/files/';
    const textIconAddr = `${fileIconBaseAddr}file.svg`;
    const pdfIconAddr = `${fileIconBaseAddr}pdf.svg`;
    const mediaIconAddr = `${fileIconBaseAddr}mp4.svg`;
    const fileTypeMappings: { [key: string]: string } = {
      pdf: pdfIconAddr,
      mp3: mediaIconAddr,
      mp4: mediaIconAddr,
      avi: mediaIconAddr,
      text: textIconAddr,
      doc: textIconAddr,
      docx: textIconAddr,
      xls: textIconAddr,
      xlsx: textIconAddr,
      csv: textIconAddr,
    };
    if (this.fileItem.filePath) {
      switch (this.fileItem.type) {
        case HelpFileTypes.File:
          this.previewFileUrl = textIconAddr;
          break;
        case HelpFileTypes.Pdf:
          this.previewFileUrl = pdfIconAddr;
          break;
        case HelpFileTypes.Image:
        case HelpFileTypes.Video:
          this.previewFileUrl = this.fileItem.filePath;
          break;
      }
      return;
    }

    // Assign predefined SVG based on the file type
    for (const [key, value] of Object.entries(fileTypeMappings)) {
      if (this.file && this.file?.type?.includes(key)) {
        this.previewFileUrl = value;
        return;
      }
    }
    // Fallback for images or other file types
    if (this.file && this.fileItem.type == HelpFileTypes.Image) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewFileUrl = e.target.result;
      };
      reader.readAsDataURL(this.file);
    }
  }

  selectFileType(isRemoveFile: boolean = true): void {
    if (isRemoveFile) {
      this.previewFileUrl = '';
      this.fileUploader().clearFile();
    }

    switch (this.fileItem.type) {
      case HelpFileTypes.Pdf:
        this.allowedFileTypes = ['.pdf'];
        break;
      case HelpFileTypes.Video:
        this.allowedFileTypes = [
          '.mp3',
          '.mp4',
          '.avi',
          '.mpeg-2',
          '.mkv',
          '.mov',
        ];
        break;
      case HelpFileTypes.Image:
        this.allowedFileTypes = ['.png', '.jpg', '.jpeg', '.gif', '.svg'];
        break;
      case HelpFileTypes.File:
        this.allowedFileTypes = [
          '.txt',
          '.doc',
          '.docx',
          '.xls',
          '.xlsx',
          '.csv',
        ];
        break;
      default:
        break;
    }
  }
}
